import React from "react";
import { FaFacebook, FaTwitter, FaInstagram, FaLinkedin, FaWhatsapp, FaArrowUp, FaEnvelope, FaPhone, FaMapMarkerAlt } from "react-icons/fa";
import { InView } from 'react-intersection-observer';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const socialLinks = [
    { icon: <FaFacebook size={22} />, href: "https://facebook.com", label: "Facebook" },
    { icon: <FaTwitter size={22} />, href: "https://twitter.com", label: "Twitter" },
    { icon: <FaInstagram size={22} />, href: "https://instagram.com", label: "Instagram" },
    { icon: <FaLinkedin size={22} />, href: "https://linkedin.com", label: "LinkedIn" },
    { icon: <FaWhatsapp size={22} />, href: "https://wa.me/919849008143", label: "WhatsApp" }
  ];

  return (
    <footer className="relative bg-gradient-to-br from-gray-900 via-gray-950 to-gray-900 border-t border-gray-800">
      {/* Scroll to Top Button */}
      <button 
        onClick={scrollToTop}
        className="absolute -top-6 right-8 w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center shadow-lg hover:shadow-purple-500/50 transition-all duration-300 transform hover:scale-110 group"
      >
        <FaArrowUp size={18} className="text-white group-hover:-translate-y-1 transition-transform" />
      </button>

      <div className="container mx-auto px-6 py-16">
        
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          
          {/* Company Info */}
          <InView triggerOnce={true} threshold={0.3}>
            {({ inView, ref }) => (
              <div ref={ref} className={`space-y-6 ${inView ? 'animate__animated animate__fadeIn' : 'opacity-0'}`}>
                <div className="flex items-center gap-3">
                  <img 
                    src="/logo1.png" 
                    alt="Edit Ezy" 
                    className="w-12 h-12" 
                  />
                  <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                    Edit Ezy
                  </span>
                </div>
                <p className="text-gray-400 leading-relaxed text-sm">
                  Your ultimate platform for creating stunning festival and business posters. Design less, share more, and grow faster with our AI-powered tools.
                </p>
                <div className="flex space-x-3">
                  {socialLinks.slice(0, 3).map((social, index) => (
                    <a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.label}
                      className="w-10 h-10 rounded-xl bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-400 hover:text-purple-400 hover:border-purple-500/50 transition-all duration-300 transform hover:scale-110 hover:shadow-lg hover:shadow-purple-500/20"
                    >
                      {social.icon}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </InView>

          {/* Quick Links */}
          <InView triggerOnce={true} threshold={0.3}>
            {({ inView, ref }) => (
              <div ref={ref} className={`space-y-6 ${inView ? 'animate__animated animate__fadeIn' : 'opacity-0'}`}
                style={{ animationDelay: '100ms' }}>
                <h3 className="text-xl font-bold text-white relative inline-block">
                  Quick Links
                  <span className="absolute bottom-0 left-0 w-1/2 h-0.5 bg-gradient-to-r from-purple-500 to-pink-500"></span>
                </h3>
                <ul className="space-y-3">
                  {[
                    { text: 'Home', link: '/' },
                    { text: 'Features', link: '/#features' },
                    { text: 'Pricing', link: '/price' },
                    { text: 'About Us', link: '/#aboutus' },
                    { text: 'Contact Us', link: '/#contactus' },
                    { text: 'Download App', link: '/#download' }
                  ].map((item, index) => (
                    <li key={index}>
                      <a 
                        href={item.link}
                        className="text-gray-400 hover:text-purple-400 transition-all duration-200 flex items-center gap-2 group"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-600 group-hover:bg-purple-500 transition-colors"></span>
                        {item.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </InView>

          {/* Our Services */}
          <InView triggerOnce={true} threshold={0.3}>
            {({ inView, ref }) => (
              <div ref={ref} className={`space-y-6 ${inView ? 'animate__animated animate__fadeIn' : 'opacity-0'}`}
                style={{ animationDelay: '200ms' }}>
                <h3 className="text-xl font-bold text-white relative inline-block">
                  Our Services
                  <span className="absolute bottom-0 left-0 w-1/2 h-0.5 bg-gradient-to-r from-purple-500 to-pink-500"></span>
                </h3>
                <ul className="space-y-3">
                  {[
                    'Festival Poster Design',
                    'Business Card Creation',
                    'AI Background Removal',
                    'Logo Making',
                    'Social Media Posters',
                    'Event Promotion'
                  ].map((service, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"></span>
                      <span className="text-gray-400 hover:text-gray-300 transition-colors text-sm cursor-default">
                        {service}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </InView>

          {/* Contact Info */}
          <InView triggerOnce={true} threshold={0.3}>
            {({ inView, ref }) => (
              <div ref={ref} className={`space-y-6 ${inView ? 'animate__animated animate__fadeIn' : 'opacity-0'}`}
                style={{ animationDelay: '300ms' }}>
                <h3 className="text-xl font-bold text-white relative inline-block">
                  Get in Touch
                  <span className="absolute bottom-0 left-0 w-1/2 h-0.5 bg-gradient-to-r from-purple-500 to-pink-500"></span>
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3 group cursor-pointer">
                    <div className="w-10 h-10 rounded-xl bg-gray-800 border border-gray-700 flex items-center justify-center flex-shrink-0 group-hover:border-purple-500/50 transition-all">
                      <FaPhone size={16} className="text-purple-400" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Phone</p>
                      <p className="text-gray-300 font-semibold">+91 984 900 8143</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 group cursor-pointer">
                    <div className="w-10 h-10 rounded-xl bg-gray-800 border border-gray-700 flex items-center justify-center flex-shrink-0 group-hover:border-purple-500/50 transition-all">
                      <FaEnvelope size={16} className="text-purple-400" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Email</p>
                      <p className="text-gray-300 font-semibold">businessbadavo@gmail.com</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 group cursor-pointer">
                    <div className="w-10 h-10 rounded-xl bg-gray-800 border border-gray-700 flex items-center justify-center flex-shrink-0 group-hover:border-purple-500/50 transition-all">
                      <FaMapMarkerAlt size={16} className="text-purple-400" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Available</p>
                      <p className="text-gray-300 font-semibold">9 AM - 5 PM IST</p>
                    </div>
                  </div>
                </div>

                {/* Newsletter Signup */}
                {/* <div className="pt-4">
                  <p className="text-gray-400 text-sm mb-3">Stay updated with latest features</p>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      placeholder="Enter your email"
                      className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all outline-none"
                    />
                    <button className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl text-white font-semibold text-sm hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300">
                      Join
                    </button>
                  </div>
                </div> */}
              </div>
            )}
          </InView>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8 mt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Edit Ezy. All Rights Reserved. 
              <span className="mx-2">|</span>
              Design Less. Share More. Grow Faster.
            </p>
            
            <div className="flex items-center gap-4">
              <a href="/privacy-policy" className="text-gray-500 hover:text-purple-400 text-sm transition-colors">Privacy Policy</a>
              <span className="text-gray-700">|</span>
              {/* <a href="/terms-and-conditions" className="text-gray-500 hover:text-purple-400 text-sm transition-colors">Terms of Service</a> */}
              
              <div className="hidden md:flex items-center gap-2 ml-4">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="w-9 h-9 rounded-lg bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-400 hover:text-purple-400 hover:border-purple-500/50 transition-all duration-300 transform hover:scale-110"
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-purple-900/10 to-transparent pointer-events-none"></div>
    </footer>
  );
};

export default Footer;